import { useEffect, useState } from 'react';
import { NotFound } from '../api/states';

export type MapRequest<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'missing' }
  | { status: 'error'; error: Error };

/**
 * Loads any map-shaped payload — state boundaries, partner pins — aborting
 * the in-flight request when the caller unmounts or asks for a different
 * one.
 *
 * `missing` is kept distinct from `error`: a 404 from the detail endpoint means
 * this organization isn't registered in that state, which is a page to render,
 * not a failure to report.
 */
export function useStateMap<T>(load: (signal: AbortSignal) => Promise<T>, deps: unknown[]) {
  const [request, setRequest] = useState<MapRequest<T>>({ status: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    setRequest({ status: 'loading' });

    load(controller.signal)
      .then((data) => setRequest({ status: 'ready', data }))
      .catch((error: Error) => {
        if (controller.signal.aborted) return;
        setRequest(error instanceof NotFound ? { status: 'missing' } : { status: 'error', error });
      });

    return () => controller.abort();
    // `load` is a fresh closure each render; the caller's deps are what actually
    // identify the request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return request;
}
